import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import {
  levelFocusApi,
  type LevelFocusResponse,
  type LevelStreamPair,
} from '@/services/levelFocusApi';

import { useAuthenticatedCall } from './useAuthenticatedCall';

export interface UseLevelFocusResult {
  /** Initial read in flight. */
  isLoading: boolean;
  /**
   * The level the server serves when it is not the profile pair. Always null
   * for a student: a student has one level per subject, set by their class or
   * enrolment, and never chooses it.
   */
  value: LevelStreamPair | null;
  /** The student's profile pair, or null until the first read resolves. */
  ownLevelStream: LevelStreamPair | null;
}

export const LEVEL_FOCUS_QUERY_KEY = ['practice', 'focus-level'] as const;

/** Stable cache-key representation of one level/stream pair. */
export const levelFocusKey = (pair: LevelStreamPair): string =>
  `${pair.orgLevel}|${pair.examLevel}`;

/**
 * Reads the level practice is served at.
 *
 * Read controllers never accept this pair as a query parameter. Consumers
 * put `levelFocusKey(value ?? ownLevelStream)` in their react-query key only;
 * the server reads the authoritative value from the User row. That cache slot
 * keeps a surface from repainting another syllabus's cached content.
 */
export function useLevelFocus(): UseLevelFocusResult {
  const { isAuthenticated, user } = useAuth0();
  const { callWithAuth } = useAuthenticatedCall();

  // Per-user scoping prevents a warm cache crossing a sign-out/sign-in.
  const queryKey = useMemo(
    () => [...LEVEL_FOCUS_QUERY_KEY, user?.sub ?? null],
    [user?.sub]
  );

  const query = useQuery<LevelFocusResponse>({
    queryKey,
    enabled: isAuthenticated,
    // A level changes with a class or enrolment, about once a year; a
    // page-to-page walk should share the record instead of re-reading it.
    staleTime: 5 * 60 * 1000,
    queryFn: () => callWithAuth(token => levelFocusApi.get(token)),
  });

  return {
    isLoading: query.isLoading,
    value: query.data?.focusLevelStream ?? null,
    ownLevelStream: query.data?.ownLevelStream ?? null,
  };
}
